/**
 * AuthContext.jsx — Real authentication context using TapstarQR Django REST API
 * ==============================================================================
 * Drops the mock base44 client and calls /api/v1/auth/* instead.
 * Context API is 100% compatible with the original so all pages still work.
 */

import React, { createContext, useState, useContext, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import { authService } from '@/api/authService';
import { adminService } from '@/api/productService';
import { enterWithSupportAccess } from '@/api/supportAccessService';
import { queryClientInstance } from '@/lib/query-client';
import { buildLoginUrl } from '@/lib/authRedirect';

const AuthContext = createContext();

// Helper compartido: adjunta el user al scope de Sentry (decisiones §40 — PII
// completa email + mongo_id por legitimate interest soporte). Passing null
// limpia el scope (importante en logout para que eventos posteriores no
// arrastren la identidad anterior).
function applySentryUser(user) {
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: user.id || user.email,
    email: user.email,
    username: user.email,
  });
}

// PostHog identify (decisiones §41). distinct_id = mongo_id estable para
// linkear sesiones anónimas previas al login con el user real. Pasamos email
// como person property (búsquedas / segmentaciones). En logout llamamos
// posthog.reset() para abandonar la identidad y empezar una sesión nueva.
// Nota: quién se graba (session replay) NO se decide aquí — es
// `PosthogRecordingGate.jsx`, que excluye admins, impersonación y las rutas
// públicas de escaneo. Aquí solo va la identidad.
function applyPosthogUser(user) {
  if (!posthog.__loaded) return;
  if (!user) {
    posthog.reset();
    return;
  }
  posthog.identify(user.id || user.email, {
    email: user.email,
  });
}

// Una sola navegación al login por carga de página. Es de módulo (no estado)
// porque el guard llama a `navigateToLogin` durante el render, donde un
// setState sería un efecto secundario prohibido — y porque la navegación es
// dura: en cuanto el navegador se mueve, el módulo se reinicia entero.
let yendoAlLogin = false;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isLoadingPublicSettings, setIsLoadingPublicSettings] = useState(false);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    checkUserAuth();
  }, []);

  // Devuelve el user (o null): quien lo llama a veces necesita el documento en
  // el acto y no puede esperar al re-render que provoca `setUser`.
  const checkUserAuth = async () => {
    try {
      setIsLoadingAuth(true);
      const currentUser = await authService.me();
      setUser(currentUser);
      applySentryUser(currentUser);
      applyPosthogUser(currentUser);
      setIsAuthenticated(true);
      setAuthError(null);
      return currentUser;
    } catch (error) {
      // 401 → not logged in, which is normal
      setIsAuthenticated(false);
      setUser(null);
      applySentryUser(null);
      applyPosthogUser(null);
      return null;
    } finally {
      setIsLoadingAuth(false);
    }
  };

  const login = async (email, password) => {
    try {
      setIsLoadingAuth(true);
      const result = await authService.login(email, password);
      // Admin con 2FA configurado: backend NO completa el auth_login todavía,
      // solo guarda pre_2fa_user_id en sesión. NO marcamos isAuthenticated
      // hasta que verify2FA tenga éxito — si no, rutas privadas dejarían
      // pasar al user sin completar el segundo factor.
      if (result.requires_2fa) {
        setAuthError(null);
        return { success: true, requires_2fa: true };
      }
      // Admin sin 2FA configurado: backend ya hizo auth_login y devuelve user.
      // Propagamos el flag para que Login.jsx redirija a setup.
      setUser(result.user);
      applySentryUser(result.user);
      applyPosthogUser(result.user);
      setIsAuthenticated(true);
      setAuthError(null);
      // Se devuelve también el user para que Login.jsx pueda decidir el destino
      // sin esperar al re-render (`user` del contexto aún va un paso por detrás).
      return { success: true, requires_2fa_setup: !!result.requires_2fa_setup, user: result.user };
    } catch (error) {
      setIsAuthenticated(false);
      // Propagamos el `code` estable del backend; la traducción se resuelve en
      // el componente (Login.jsx) vía apiMessage — AuthProvider está por encima
      // de LanguageProvider y no tiene acceso a `t`.
      return {
        success: false,
        code: error?.response?.data?.code || null,
        message: error?.response?.data?.error || error?.message || null,
      };
    } finally {
      setIsLoadingAuth(false);
    }
  };

  const logout = async (shouldRedirect = true) => {
    try {
      await authService.logout();
    } catch (_) { /* ignore */ }
    setUser(null);
    applySentryUser(null);
    applyPosthogUser(null);
    setIsAuthenticated(false);
    if (shouldRedirect) navigateToLogin();
  };

  /**
   * Manda al login. `redirectTo` (opcional) es la ruta interna a la que volver
   * tras autenticarse — la valida `buildLoginUrl`, así que aquí no hace falta
   * comprobar nada. `logout()` lo llama SIN destino a propósito: al cerrar
   * sesión no se vuelve a la página privada de la que se sale.
   *
   * Ojo al engancharlo a un onClick: `navigateToLogin` recibiría el evento como
   * `redirectTo`. El saneado lo descartaría por no ser string, pero mejor no
   * depender de eso — envuélvelo en una lambda.
   *
   * @param {string|{pathname?: string, search?: string, hash?: string}|null} [redirectTo]
   */
  const navigateToLogin = (redirectTo = null) => {
    // El guard de App.jsx llama a esto DURANTE el render y puede repetirse en
    // varios renders seguidos antes de que el navegador se mueva. Reasignar
    // `href` no rompe nada, pero ensucia la navegación en Sentry/PostHog.
    if (yendoAlLogin) return;
    yendoAlLogin = true;
    window.location.href = buildLoginUrl(redirectTo);
  };

  const updateUser = async (fields) => {
    try {
      setIsLoadingAuth(true);
      const updatedUser = await authService.updateMe(fields);
      setUser(updatedUser);
      applySentryUser(updatedUser);
      applyPosthogUser(updatedUser);
      setAuthError(null);
      return { success: true, user: updatedUser };
    } catch (error) {
      return {
        success: false,
        code: error?.response?.data?.code || null,
        message: error?.response?.data?.error || error?.message || null,
      };
    } finally {
      setIsLoadingAuth(false);
    }
  };

  // 2FA — passthrough a authService. TwoFactorSetup.jsx y Login.jsx los
  // consumen vía useAuth(). El único método con efecto en el contexto es
  // verify2FA: tras éxito el backend completa el auth_login y la siguiente
  // request /auth/me/ devuelve al user — refrescamos identidad en Sentry /
  // PostHog y marcamos isAuthenticated para que las rutas privadas pasen.
  const get2FAStatus     = () => authService.get2FAStatus();
  const setup2FA         = () => authService.setup2FA();
  const confirm2FASetup  = (deviceId, token) => authService.confirm2FASetup(deviceId, token);
  const disable2FA       = (token) => authService.disable2FA(token);
  const verify2FA = async (token) => {
    try {
      await authService.verify2FA(token);
      // refresca user + isAuthenticated + Sentry/PostHog, y devuelve el doc para
      // que Login.jsx resuelva el destino igual que en el login sin 2FA.
      const currentUser = await checkUserAuth();
      return { success: true, user: currentUser };
    } catch (error) {
      return {
        success: false,
        code: error?.response?.data?.code || null,
        message: error?.response?.data?.error || error?.message || null,
      };
    }
  };

  // ── Impersonación de soporte ───────────────────────────────────────────
  // Entrar y salir hacen SIEMPRE `queryClient.clear()` + recarga dura. La caché
  // de react-query guarda el `['gbusiness','me']` del admin, sus dispositivos y
  // sus métricas: sin limpiarla, al entrar se verían datos del admin dentro de
  // la cuenta del cliente y, peor, al salir se verían datos del cliente en la
  // sesión del admin. Eso es un incidente de privacidad, no un fallo de UI. La
  // recarga es la forma más barata de garantizar que no queda estado en memoria.
  const resetAndReload = (destination) => {
    queryClientInstance.clear();
    window.location.href = destination;
  };

  const startImpersonation = async (userId) => {
    await adminService.startImpersonation(userId);
    resetAndReload('/');
  };

  // Entrar con permiso del usuario: el backend consume el permiso y abre la
  // MISMA impersonación de siempre, así que el resto es idéntico.
  const enterWithConsent = async (requestId) => {
    await enterWithSupportAccess(requestId);
    resetAndReload('/');
  };

  // Al salir se vuelve a la ficha de admin del cliente en el que se estaba, que
  // es desde donde se entró. `targetId` es su `_id` de Mongo, el mismo que usa
  // `/AdminUserDetail/:id`. Se lee ANTES de cerrar la sesión: después, `/me` ya
  // no trae el bloque de impersonación. Sin él, al listado.
  const exitDestination = () => {
    const targetId = user?.impersonation?.targetId;
    return targetId ? `/AdminUserDetail/${encodeURIComponent(targetId)}` : '/AdminUsers';
  };

  // El usuario retiró el acceso (o la sesión se cerró por otro motivo): se
  // vuelve al panel con el mismo vaciado de caché que al salir a mano.
  const leaveImpersonation = () => resetAndReload(exitDestination());

  const stopImpersonation = async () => {
    const destination = exitDestination();
    await adminService.stopImpersonation();
    resetAndReload(destination);
  };

  // Alternar el modo fiel cambia lo que responde CADA endpoint: el plan, los
  // gates, el saldo. Por eso pasa por `resetAndReload` como entrar y salir —
  // una caché a medio invalidar mezclaría la vista de admin con la del cliente,
  // que es un estado que no existe en la realidad y se lee como un fallo del
  // producto. Se recarga la MISMA página para no perder de vista lo que se
  // estaba mirando, que es justo lo que se viene a comparar.
  const setImpersonationViewMode = async (enabled) => {
    await adminService.setImpersonationViewMode(enabled);
    resetAndReload(window.location.pathname + window.location.search);
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      isLoadingAuth,
      isLoadingPublicSettings,
      authError,
      appPublicSettings: null,  // kept for backward compat
      login,
      logout,
      updateUser,
      navigateToLogin,
      checkAppState: checkUserAuth,
      // Impersonación de soporte
      impersonation: user?.impersonation || null,
      startImpersonation,
      enterWithConsent,
      leaveImpersonation,
      stopImpersonation,
      setImpersonationViewMode,
      // 2FA
      get2FAStatus,
      setup2FA,
      confirm2FASetup,
      disable2FA,
      verify2FA,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
